/**
 * Nhật ký sự kiện trình duyệt (eventJournal) — chuẩn Feedback EMS v2 §11
 *
 * - sessionId = uuid mỗi tab (sessionStorage), gửi kèm mọi request qua header x-session-id; mỗi request có x-request-id mới.
 * - Bộ đệm vòng 100 sự kiện trong sessionStorage (không mất khi tải lại trang).
 * - Gửi lô ≤ 50 sự kiện lên tRPC userEvents.ingest mỗi 10 giây và khi pagehide / tab ẩn (fetch keepalive).
 *   Gửi lỗi thì giữ lại lần sau; không bao giờ làm hỏng app.
 * - Không bao giờ ghi giá trị ô nhập. Che token, mật khẩu, cookie, Authorization, email, SĐT, MST/CCCD.
 */
import { useAuthStore } from '@/stores/auth.store';
import { changelogData } from '@/config/changelog-data';

export type JournalKind = 'route' | 'click' | 'submit' | 'api' | 'console' | 'error';
export type JournalLevel = 'info' | 'warn' | 'error';

export interface JournalEvent {
  id: string;
  occurredAt: string;
  kind: JournalKind;
  name: string;
  level: JournalLevel;
  status?: number | null;
  durationMs?: number | null;
  requestId?: string | null;
  payload?: Record<string, unknown> | null;
  appVersion?: string;
  /** Đã gửi lên server */
  _s?: 1;
}

const STORAGE_PREFIX = 'apv.journal.v2';
const SESSION_KEY = `${STORAGE_PREFIX}.sid`;
const EVENTS_KEY = `${STORAGE_PREFIX}.events`;
export const JOURNAL_MAX_EVENTS = 100;
export const JOURNAL_FLUSH_BATCH = 50;
export const JOURNAL_FLUSH_INTERVAL_MS = 10_000;
const NAME_MAX = 200;
const KEEPALIVE_MAX_BYTES = 60_000;

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3006/api';
const INGEST_URL = `${API_BASE_URL}/trpc/userEvents.ingest`;

let memorySessionId: string | null = null;
let memoryEvents: JournalEvent[] | null = null;
let persistTimer: ReturnType<typeof setTimeout> | null = null;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let flushing = false;
let started = false;

// ─── Che dữ liệu nhạy cảm ───────────────────────────────────────────────
const BEARER = /Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi;
const JWT_LIKE = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g;
const KV_SECRET = /\b(password|passwd|pwd|token|accessToken|refreshToken|secret|otp|cookie|authorization|cccd)\s*["']?\s*[:=]\s*(['"]?)[^\s"',}&]+\2/gi;
const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const PHONE = /(?:\+?84|\b0)(?:[\s.-]?\d){9,10}\b/g;
const LONG_DIGITS = /\b\d{10}(?:-\d{3})?\b|\b\d{12,13}\b/g;
const SENSITIVE_KEY = /(password|passwd|pwd|token|secret|authorization|cookie|otp|cccd|cmnd|api[_-]?key|phone|email|^value$|^values$)/i;

export function maskJournalText(input: unknown, maxLen = NAME_MAX): string {
  if (input === null || input === undefined) return '';
  const str = typeof input === 'string' ? input : input instanceof Error ? `${input.name}: ${input.message}` : String(input);
  return str
    .replace(BEARER, 'Bearer [REDACTED]')
    .replace(JWT_LIKE, '[JWT]')
    .replace(KV_SECRET, '$1=[REDACTED]')
    .replace(EMAIL, '[EMAIL]')
    .replace(PHONE, '[PHONE]')
    .replace(LONG_DIGITS, '[ID]')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLen);
}

function maskPayload(payload: Record<string, unknown> | null | undefined): Record<string, unknown> | null {
  if (!payload) return null;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(payload).slice(0, 15)) {
    if (v === undefined) continue;
    if (SENSITIVE_KEY.test(k)) {
      out[k] = '[REDACTED]';
    } else if (typeof v === 'number' || typeof v === 'boolean' || v === null) {
      out[k] = v;
    } else {
      out[k] = maskJournalText(typeof v === 'string' ? v : JSON.stringify(v), 600);
    }
  }
  return out;
}

// ─── Mã phiên / mã request ──────────────────────────────────────────────
export function newJournalId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    // ignore
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`;
}

function safeSessionGet(key: string): string | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, value);
  } catch {
    // hết dung lượng / chế độ riêng tư: chỉ giữ trong bộ nhớ
  }
}

/** Mã phiên của tab hiện tại (tạo mới nếu chưa có) */
export function getJournalSessionId(): string {
  if (memorySessionId) return memorySessionId;
  const stored = safeSessionGet(SESSION_KEY);
  memorySessionId = stored && /^[A-Za-z0-9._:-]{6,80}$/.test(stored) ? stored : newJournalId();
  if (!stored) safeSessionSet(SESSION_KEY, memorySessionId);
  return memorySessionId;
}

/** Header truy vết gắn vào mỗi request (request id mới mỗi lần) */
export function getTraceHeaders(): { 'x-session-id': string; 'x-request-id': string } {
  return { 'x-session-id': getJournalSessionId(), 'x-request-id': newJournalId() };
}

// ─── Bộ đệm vòng ────────────────────────────────────────────────────────
function loadEvents(): JournalEvent[] {
  if (memoryEvents) return memoryEvents;
  try {
    const raw = safeSessionGet(EVENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    memoryEvents = Array.isArray(parsed) ? parsed.slice(-JOURNAL_MAX_EVENTS) : [];
  } catch {
    memoryEvents = [];
  }
  return memoryEvents!;
}

function persistSoon(immediate = false): void {
  if (immediate) {
    if (persistTimer) {
      clearTimeout(persistTimer);
      persistTimer = null;
    }
    safeSessionSet(EVENTS_KEY, JSON.stringify(loadEvents()));
    return;
  }
  if (persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    safeSessionSet(EVENTS_KEY, JSON.stringify(loadEvents()));
  }, 250);
}

function appVersion(): string {
  return changelogData?.[0]?.version || '';
}

/** Ghi một sự kiện vào nhật ký (không ném lỗi) */
export function recordJournalEvent(input: {
  kind: JournalKind;
  name: string;
  level?: JournalLevel;
  status?: number | null;
  durationMs?: number | null;
  requestId?: string | null;
  payload?: Record<string, unknown> | null;
}): JournalEvent | null {
  try {
    const name = maskJournalText(input.name, NAME_MAX);
    if (!name) return null;
    const ev: JournalEvent = {
      id: newJournalId(),
      occurredAt: new Date().toISOString(),
      kind: input.kind,
      name,
      level: input.level || 'info',
      status: input.status ?? null,
      durationMs: input.durationMs != null ? Math.max(0, Math.round(input.durationMs)) : null,
      requestId: input.requestId || null,
      payload: maskPayload(input.payload),
      appVersion: appVersion(),
    };
    const events = loadEvents();
    events.push(ev);
    if (events.length > JOURNAL_MAX_EVENTS) events.splice(0, events.length - JOURNAL_MAX_EVENTS);
    persistSoon();
    return ev;
  } catch {
    return null;
  }
}

/** N sự kiện gần nhất (bỏ cờ nội bộ) — dùng cho ảnh chụp chẩn đoán của nút Phản hồi */
export function getJournalEvents(limit = 50): Omit<JournalEvent, '_s'>[] {
  return loadEvents()
    .slice(-limit)
    .map(({ _s, ...rest }) => rest);
}

export function clearJournal(): void {
  memoryEvents = [];
  persistSoon(true);
}

// ─── Ghi lời gọi API ─────────────────────────────────────────────────────
/** Tên lời gọi API không kèm giá trị query: tRPC → danh sách procedure, REST → METHOD /path */
export function apiEventName(method: string, rawUrl: string): string {
  const m = (method || 'GET').toUpperCase();
  let path = String(rawUrl || '');
  try {
    path = new URL(path, typeof window !== 'undefined' ? window.location.origin : 'http://localhost').pathname;
  } catch {
    path = path.split('?')[0];
  }
  const trpcIdx = path.indexOf('/trpc/');
  if (trpcIdx >= 0) {
    return `${m} trpc:${decodeURIComponent(path.slice(trpcIdx + 6))}`.slice(0, NAME_MAX);
  }
  // Thay id động bằng :id để không lộ mã cụ thể
  const norm = path
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id')
    .replace(/\/c[a-z0-9]{20,30}/gi, '/:id')
    .replace(/\/\d+(?=\/|$)/g, '/:id');
  return `${m} ${norm}`.slice(0, NAME_MAX);
}

// ─── Trang đang rời đi ───────────────────────────────────────────────────
// Tải lại / rời trang huỷ các request đang chạy, trình duyệt báo "Failed to fetch" dù máy chủ vẫn chạy.
// Không ghi các request đó là lỗi mạng, nếu không máy chủ mở phiếu "Mất kết nối" giả (FB-0HP9YF, 03/11/2026).
let pageLeaving = false;

/** Đánh dấu trang đang rời đi (beforeunload / pagehide); bỏ đánh dấu khi trang được khôi phục từ bộ nhớ đệm */
export function markPageLeaving(leaving = true): void {
  pageLeaving = leaving;
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => markPageLeaving(true));
  window.addEventListener('pagehide', () => markPageLeaving(true));
  window.addEventListener('pageshow', () => markPageLeaving(false));
}

export function recordApiEvent(params: {
  method: string;
  url: string;
  status: number | 'NETWORK_ERROR';
  durationMs: number;
  requestId?: string | null;
  message?: string;
}): void {
  if (String(params.url || '').includes('userEvents.ingest')) return;
  const statusNum = typeof params.status === 'number' ? params.status : 0;
  const cancelledByNavigation = params.status === 'NETWORK_ERROR' && pageLeaving;
  const level: JournalLevel = cancelledByNavigation
    ? 'info'
    : params.status === 'NETWORK_ERROR' || statusNum >= 500 ? 'error' : statusNum >= 400 ? 'warn' : 'info';
  recordJournalEvent({
    kind: 'api',
    name: apiEventName(params.method, params.url),
    level,
    status: statusNum || null,
    durationMs: params.durationMs,
    requestId: params.requestId || null,
    payload: cancelledByNavigation
      ? { message: params.message || 'Huỷ khi rời trang', cancelledByNavigation: true }
      : params.message && level !== 'info' ? { message: params.message } : null,
  });
}

// ─── Gửi lô lên server ───────────────────────────────────────────────────
function buildBody(events: JournalEvent[]): string {
  return JSON.stringify({
    json: {
      sessionId: getJournalSessionId(),
      events: events.map(({ _s, ...e }) => ({
        ...e,
        payload: e.payload || undefined,
        status: e.status ?? undefined,
        durationMs: e.durationMs ?? undefined,
        requestId: e.requestId || undefined,
      })),
    },
  });
}

/** Gửi tối đa 50 sự kiện chưa gửi; trả về số sự kiện đã gửi */
export async function flushJournal(opts: { keepalive?: boolean } = {}): Promise<number> {
  if (flushing) return 0;
  const token = useAuthStore.getState()?.accessToken;
  if (!token) return 0;
  const events = loadEvents();
  let batch = events.filter((e) => !e._s).slice(0, JOURNAL_FLUSH_BATCH);
  if (batch.length === 0) return 0;
  let body = buildBody(batch);
  if (opts.keepalive) {
    while (body.length > KEEPALIVE_MAX_BYTES && batch.length > 1) {
      batch = batch.slice(0, Math.ceil(batch.length / 2));
      body = buildBody(batch);
    }
  }
  flushing = true;
  try {
    const res = await fetch(INGEST_URL, {
      method: 'POST',
      keepalive: !!opts.keepalive,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...getTraceHeaders(),
      },
      body,
    });
    if (!res.ok) return 0;
    const sent = new Set(batch.map((e) => e.id));
    for (const e of events) if (sent.has(e.id)) e._s = 1;
    persistSoon(true);
    return batch.length;
  } catch {
    return 0; // giữ lại lần sau
  } finally {
    flushing = false;
  }
}

/** Bắt đầu gửi định kỳ + khi rời trang (gọi 1 lần trong installDiagnosticsCollector) */
export function startEventJournal(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  getJournalSessionId();
  flushTimer = setInterval(() => {
    void flushJournal().then((n) => {
      // Còn tồn nhiều thì gửi tiếp ngay
      if (n === JOURNAL_FLUSH_BATCH) void flushJournal();
    });
  }, JOURNAL_FLUSH_INTERVAL_MS);
  const onHide = () => {
    persistSoon(true);
    void flushJournal({ keepalive: true });
  };
  window.addEventListener('pagehide', onHide);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') onHide();
  });
}

export function stopEventJournal(): void {
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = null;
  started = false;
}
